import { apiClient, API_ENDPOINTS, ApiResponse, USE_MOCK, delay } from './client';

export interface RoomMapping {
  id: string;
  yourVariantId: number;
  yourVariantName: string;
  yourRoomTypeName: string;
  competitorProperty: string;
  competitorRoomName: string;
  competitorPlatform: string;
  matchConfidence: number;
  isManualOverride: boolean;
  createdAt: string;
}

export interface CompetitorRoomMappingPayload {
  resource_name: 'CompetitorRoomMapping';
  column_value_pair: Array<{
    branch_id: number;
    competitor_hotel_name: string;
    branch_name: string;
    mappings: string;
  }>;
}

export interface RoomMappingsResponse {
  success: boolean;
  data: RoomMapping[];
}

// Mock storage for development
let mockMappings: RoomMapping[] = [];

/**
 * Fetch all room mappings for analysis
 */
export async function fetchRoomMappings(branchId: number): Promise<ApiResponse<RoomMapping[]>> {
  if (USE_MOCK) {
    await delay();
    return {
      success: true,
      data: mockMappings,
    };
  }

  try {
    const filters = JSON.stringify([
      { resource_name: 'CompetitorRoomMapping', branch_id: branchId }
    ]);
    const response = await apiClient.get(`${API_ENDPOINTS.generic}?filters=${encodeURIComponent(filters)}`);
    
    // Parse the mappings from the response
    const rawData = response.data?.data || [];
    const mappings: RoomMapping[] = [];
    
    for (const item of rawData) {
      if (item.mappings) {
        try {
          const parsed = typeof item.mappings === 'string' ? JSON.parse(item.mappings) : item.mappings;
          mappings.push(...(Array.isArray(parsed) ? parsed : []));
        } catch {
          console.warn('Failed to parse mappings:', item.mappings);
        }
      }
    }
    
    return {
      success: true,
      data: mappings,
    };
  } catch (error: any) {
    console.error('Error fetching room mappings:', error);
    return {
      success: false,
      data: [],
      error: error.message,
    };
  }
}

/**
 * Save room mappings using generic endpoint
 */
export interface SaveRoomMappingsParams {
  branchId: number;
  branchName: string;
  competitorHotelName: string;
  mappings: RoomMapping[];
}

/**
 * Save room mappings using generic endpoint with CompetitorRoomMapping resource
 */
export async function saveRoomMappings(
  params: SaveRoomMappingsParams
): Promise<ApiResponse<RoomMapping[]>> {
  const { branchId, branchName, competitorHotelName, mappings } = params;

  if (USE_MOCK) {
    await delay();
    mockMappings = mappings;
    return {
      success: true,
      data: mappings,
    };
  }

  try {
    const payload: CompetitorRoomMappingPayload[] = [
      {
        resource_name: 'CompetitorRoomMapping',
        column_value_pair: [
          {
            branch_id: branchId,
            competitor_hotel_name: competitorHotelName,
            branch_name: branchName,
            mappings: JSON.stringify(mappings),
          },
        ],
      },
    ];

    await apiClient.patch(API_ENDPOINTS.generic, payload);
    return {
      success: true,
      data: mappings,
    };
  } catch (error: any) {
    console.error('Error saving room mappings:', error);
    return {
      success: false,
      data: [],
      error: error.message,
    };
  }
}

/**
 * Delete all room mappings for a property
 */
export async function deleteRoomMappings(propertyId: string): Promise<ApiResponse<void>> {
  if (USE_MOCK) {
    await delay();
    mockMappings = [];
    return {
      success: true,
      data: undefined,
    };
  }

  try {
    await apiClient.delete(`${API_ENDPOINTS.generic}?resource_name=RoomMapping&property_id=${propertyId}`);
    return {
      success: true,
      data: undefined,
    };
  } catch (error: any) {
    console.error('Error deleting room mappings:', error);
    return {
      success: false,
      data: undefined,
      error: error.message,
    };
  }
}
